/**
 * Contact Picker Utility
 * Provides functionality to select contacts from device contacts using Navigator API
 */

/**
 * Check if the Contact Picker API is available
 * @returns {boolean} True if contact picker is supported
 */
export const isContactPickerSupported = () => {
  try {
    return typeof navigator !== 'undefined' && 
           'contacts' in navigator && 
           'ContactsManager' in window
  } catch (error) {
    console.warn('Error checking contact picker support:', error)
    return false
  }
}

/**
 * Select a contact from device contacts
 * @param {Object} options - Contact selection options
 * @param {string[]} options.properties - Contact properties to retrieve ['name', 'tel', 'email', 'address', 'icon']
 * @param {boolean} options.multiple - Whether to allow multiple contact selection
 * @returns {Promise<Object[]>} Array of selected contacts
 */
export const selectContacts = async (options = {}) => {
  try {
    // Check if API is supported
    if (!isContactPickerSupported()) {
      throw new Error('Contact Picker API is not supported on this device')
    }

    const defaultOptions = {
      properties: ['name', 'tel', 'email'],
      multiple: false
    }

    const config = { ...defaultOptions, ...options }

    // Request contact selection
    const contacts = await navigator.contacts.select(config.properties, { multiple: config.multiple })
    
    return contacts.map(contact => ({
      name: contact.name && contact.name.length > 0 ? contact.name[0] : '',
      phone: contact.tel && contact.tel.length > 0 ? formatPhoneNumber(contact.tel[0]) : '',
      email: contact.email && contact.email.length > 0 ? contact.email[0] : '',
      raw: contact // Keep raw contact data for debugging
    }))
  } catch (error) {
    console.error('Contact selection failed:', error)
    
    // Handle specific error cases
    if (error.name === 'InvalidStateError') {
      throw new Error('Contact picker is already open')
    } else if (error.name === 'SecurityError') {
      throw new Error('Contact access denied. Please check permissions.')
    } else if (error.name === 'AbortError') {
      throw new Error('Contact selection cancelled')
    } else {
      throw new Error(`Contact selection failed: ${error.message}`)
    }
  }
}

/**
 * Format phone number to Indian mobile format
 * @param {string} phoneNumber - Raw phone number from contact
 * @returns {string} Formatted 10-digit mobile number
 */
const formatPhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return ''
  
  // Remove all non-numeric characters
  let cleaned = phoneNumber.replace(/\D/g, '')
  
  // Handle Indian mobile numbers
  if (cleaned.length === 13 && cleaned.startsWith('91')) {
    // Remove country code +91
    cleaned = cleaned.substring(2)
  } else if (cleaned.length === 12 && cleaned.startsWith('91')) {
    // Remove country code 91
    cleaned = cleaned.substring(2)
  } else if (cleaned.length === 11 && cleaned.startsWith('0')) {
    // Remove leading 0
    cleaned = cleaned.substring(1)
  }
  
  // Validate Indian mobile number (should be 10 digits starting with 6-9)
  if (cleaned.length === 10 && /^[6-9]/.test(cleaned)) {
    return cleaned
  }
  
  // If not a valid Indian mobile, return the cleaned version (let validation handle it)
  return cleaned.length <= 10 ? cleaned : cleaned.substring(cleaned.length - 10)
}

/**
 * Check permissions for contact access
 * @returns {Promise<boolean>} True if permission is granted
 */
export const checkContactPermission = async () => {
  try {
    if (!('permissions' in navigator)) {
      return false
    }
    
    // Note: Most browsers don't have 'contacts' permission in permissions API yet
    // This is more for future compatibility
    const permission = await navigator.permissions.query({ name: 'contacts' })
    return permission.state === 'granted'
  } catch (error) {
    console.warn('Contact permission check failed:', error)
    return false
  }
}

/**
 * Show a user-friendly message about contact picker availability
 * @returns {string} Status message
 */
export const getContactPickerStatus = () => {
  if (!('navigator' in window)) {
    return 'Contact picker not available: Navigator not supported'
  }
  
  if (!('contacts' in navigator)) {
    return 'Contact picker not available: Contacts API not supported'
  }
  
  if (!('ContactsManager' in window)) {
    return 'Contact picker not available: ContactsManager not found'
  }
  
  return 'Contact picker is available'
}